import { lazy, Suspense, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { stringFromOrg } from '../../../../../../common/src/server_api.ts';
import SearchIcon from '../../../../assets/search.svg?react';
import { useCurrentOrg } from '../../../../data/use_current_org.tsx';
import { useOrgInfo } from '../../../../data/use_org_info.ts';
import { useUserAttributes } from '../../../../data/use_user_attributes.ts';
import { goBack } from '../../../../util/go_back.ts';
import { SearchControl } from '../../../../util/search_control.tsx';
import { BackArrow, BackButtonWrapper, headerHeight, OuterPageContent, Title } from '../../../../util/shared.tsx';
import { HouseholdManagementSection } from './household_management_section.tsx';
import { HouseholdFilterContext } from './households_list.tsx';
import { Sections } from './shared.tsx';

const UsageSection = lazy(() =>
  import('./usage_section.tsx').catch(() => {
    const reloadKey = 'usage_section_reload';
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.reload();
    }
    // Return a placeholder component so TypeScript is satisfied; the reload fires first.
    return { default: () => null } as unknown as typeof import('./usage_section.tsx');
  }),
);

export function Dashboard() {
  const { currentOrg } = useCurrentOrg();
  const { orgInfo, isDataRecentlySynced, updateOrgInfo } = useOrgInfo();
  const navigate = useNavigate();
  const [searchIsVisible, setSearchIsVisible] = useState(false);
  const [filterOnStaff, setFilterOnStaff] = useState(false);
  const { isInternalUser } = useUserAttributes();

  const onBack = useCallback(async () => {
    await goBack(navigate);
  }, [navigate]);

  const onSearchClick = useCallback(() => {
    setSearchIsVisible(true);
  }, []);

  const onSearchCancel = useCallback(() => {
    setSearchIsVisible(false);
  }, []);

  const onAllFilterClick = useCallback(() => {
    setFilterOnStaff(false);
  }, []);

  const onStaffFilterClick = useCallback(() => {
    setFilterOnStaff(true);
  }, []);

  return (
    <HouseholdFilterContext value={{ filterOnStaff }}>
      <OuterPageContent style={{ zIndex: 10 }}>
        {searchIsVisible ? (
          <SearchControl onCancel={onSearchCancel} />
        ) : (
          <Header>
            <TitleGroup>
              <BackButtonWrapper>
                <BackArrow onClick={onBack} />
              </BackButtonWrapper>
              <Title>
                {currentOrg
                  ? `${stringFromOrg(currentOrg)}${isInternalUser ? ` - ${currentOrg.id}` : ''}`
                  : 'Dashboard'}
              </Title>
            </TitleGroup>
            <ButtonGroup>
              <FilterButton $selected={!filterOnStaff} onClick={onAllFilterClick} $isStaff={false}>
                All
              </FilterButton>
              <FilterButton $selected={filterOnStaff} onClick={onStaffFilterClick} $isStaff={true}>
                Staff
              </FilterButton>
              <SearchIcon className="searchHeaderIcon" onClick={() => onSearchClick()} />
            </ButtonGroup>
          </Header>
        )}
        <Background>
          <Sections>
            <HouseholdManagementSection
              orgInfo={orgInfo}
              isDataRecentlySynced={isDataRecentlySynced}
              updateOrgInfo={updateOrgInfo}
            />
            <Suspense fallback={null}>
              <UsageSection orgInfo={orgInfo} orgType={currentOrg?.type} isDataRecentlySynced={isDataRecentlySynced} />
            </Suspense>
          </Sections>
        </Background>
      </OuterPageContent>
    </HouseholdFilterContext>
  );
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${headerHeight};
  border-bottom: 1px solid transparent;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Background = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100vw;
  overflow-y: scroll;
`;

const FilterButton = styled.button<{ $selected: boolean; $isStaff: boolean }>`
  font-family: Jost-SemiBold, sans-serif;
  color: ${(props) =>
    props.$selected
      ? 'var(--color-background)'
      : props.$isStaff
        ? 'var(--color-dashboard-staff)'
        : 'var(--color-text)'};
  background: ${(props) =>
    props.$selected
      ? props.$isStaff
        ? 'var(--color-dashboard-staff)'
        : 'var(--color-text)'
      : 'var(--color-background)'};
  border: 3px solid ${(props) => (props.$isStaff ? 'var(--color-dashboard-staff)' : 'var(--color-text)')};
  border-radius: 10px;
  width: 8ch;
`;
